import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderInput, FileCheck2, Clock, Archive, ScrollText, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ALL_CARDS = [
  { key: "total_deposits", label: "Total dépôts", Icon: FolderInput, color: "#0A2E52", forRoles: ["super_admin", "deposits_service"] },
  { key: "memoires", label: "Mémoires", Icon: FolderInput, color: "#144C86", forRoles: ["super_admin", "deposits_service"] },
  { key: "projets_tutorés", label: "Projets tutorés", Icon: FolderInput, color: "#D4AF37", forRoles: ["super_admin", "deposits_service"] },
  { key: "pending", label: "En cours de traitement", Icon: Clock, color: "#E63946", forRoles: ["super_admin", "deposits_service"] },
  { key: "validated", label: "Dépôts validés", Icon: FileCheck2, color: "#059669", forRoles: ["super_admin", "deposits_service", "library"] },
  { key: "published", label: "Publiés (Archives)", Icon: Archive, color: "#0A2E52", forRoles: ["super_admin", "library"] },
  { key: "legacy_submissions", label: "Anciens travaux reçus", Icon: ScrollText, color: "#D4AF37", forRoles: ["super_admin", "library"] },
];

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const role = user?.role || "";
  const cards = ALL_CARDS.filter((c) => c.forRoles.includes(role) || role === "super_admin");

  const isDeposits = role === "deposits_service";
  const isLibrary = role === "library";

  return (
    <div data-testid="dashboard-home">
      <div className="eyebrow eyebrow-dark">Tableau de bord</div>
      <h1 className="mt-2 text-3xl font-bold text-unisic-blue">
        Bienvenue, {user?.name || user?.email}
      </h1>
      <p className="mt-2 text-slate-600 text-sm">
        {isDeposits && "Service des Dépôts Académiques — gestion des mémoires et projets tutorés."}
        {isLibrary && "Service des Bibliothèques et musées — publications et travaux validés."}
        {role === "super_admin" && "Administration complète du portail UNISIC-Recherche."}
      </p>
      <span className="divider-gold mt-3" />

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.key} className="paper-card p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500">{c.label}</div>
              <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: `${c.color}14`, color: c.color }}>
                <c.Icon className="w-4 h-4" />
              </div>
            </div>
            <div className="mt-3 text-3xl font-bold text-unisic-blue">{stats[c.key] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Quick access shortcuts */}
      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {(role === "super_admin" || isDeposits) && (
          <Link to="/dashboard/depots" className="paper-card card-hover p-6 flex items-start gap-4 group">
            <div className="w-11 h-11 rounded-sm bg-unisic-blue/10 text-unisic-blue flex items-center justify-center flex-shrink-0">
              <FolderInput className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-semibold text-unisic-blue">Traiter les dépôts</h3>
              <p className="mt-1 text-sm text-slate-600">Consulter la liste des mémoires et projets, changer les statuts, lancer les tests anti-plagiat.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-unisic-gold group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
        {(role === "super_admin" || isLibrary) && (
          <Link to="/dashboard/bibliotheque" className="paper-card card-hover p-6 flex items-start gap-4 group">
            <div className="w-11 h-11 rounded-sm bg-unisic-gold/15 text-unisic-gold flex items-center justify-center flex-shrink-0">
              <Archive className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-semibold text-unisic-blue">Publier & archiver</h3>
              <p className="mt-1 text-sm text-slate-600">Diffuser les travaux validés et évaluer les anciens travaux soumis par les diplômés.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-unisic-gold group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>
    </div>
  );
}
